import { useState } from 'react';
import { useDeleteMatch, useSaveMatch } from '../api/hooks';
import type { Match, MatchRequest, Player, PlayerPosition, Tournament } from '../api/types';
import ErrorBanner from './ErrorBanner';
import GeneratePosterDialog from './GeneratePosterDialog';

const POSITIONS: PlayerPosition[] = ['Unspecified', 'Lead', 'Second', 'Third', 'Skip'];

/** datetime-local wants "YYYY-MM-DDTHH:mm" in local time, not the ISO string the API returns. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function blankForm(tournament: Tournament): MatchRequest {
  return {
    week: 1,
    roundName: '',
    startsAt: toLocalInput(new Date().toISOString()),
    venue: tournament.venue ?? '',
    opponent: '',
    isHome: true,
    players: [],
    notes: '',
  };
}

function toForm(match: Match): MatchRequest {
  return {
    week: match.week,
    roundName: match.roundName ?? '',
    startsAt: toLocalInput(match.startsAt),
    venue: match.venue ?? '',
    opponent: match.opponent,
    isHome: match.isHome,
    players: match.players,
    notes: match.notes ?? '',
  };
}

export default function MatchEditor({
  tournament,
  matches,
  isLoading,
  onPosterQueued,
}: {
  tournament: Tournament;
  matches: Match[];
  isLoading: boolean;
  /** Sends the page to the Posters tab, where the queued poster is about to appear. */
  onPosterQueued: () => void;
}) {
  const [editingId, setEditingId] = useState<string | undefined>();
  const [form, setForm] = useState<MatchRequest | undefined>();
  const [generatingFor, setGeneratingFor] = useState<Match | undefined>();

  const saveMatch = useSaveMatch(tournament.id);
  const deleteMatch = useDeleteMatch(tournament.id);

  const startNew = () => {
    setEditingId(undefined);
    setForm(blankForm(tournament));
  };

  const startEdit = (match: Match) => {
    setEditingId(match.id);
    setForm(toForm(match));
  };

  const updatePlayer = (index: number, patch: Partial<Player>) => {
    if (!form) return;
    setForm({ ...form, players: form.players.map((p, i) => (i === index ? { ...p, ...patch } : p)) });
  };

  /** The list order is the numbering printed down the poster, so it has to be editable. */
  const movePlayer = (index: number, delta: number) => {
    if (!form) return;
    const target = index + delta;
    if (target < 0 || target >= form.players.length) return;

    const players = [...form.players];
    const moved = players[index]!;
    players[index] = players[target]!;
    players[target] = moved;
    setForm({ ...form, players });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) return;

    await saveMatch.mutateAsync({
      matchId: editingId,
      body: {
        ...form,
        // The input is local time; send a real instant so the poster prints the right hour.
        startsAt: new Date(form.startsAt).toISOString(),
        players: form.players.filter((p) => p.name.trim().length > 0),
      },
    });

    setForm(undefined);
    setEditingId(undefined);
  };

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Fixtures</h2>
        <button type="button" className="button button--primary" onClick={startNew}>
          Add fixture
        </button>
      </div>

      {isLoading && <p className="muted">Loading fixtures...</p>}

      {!isLoading && matches.length === 0 && !form && (
        <div className="card empty">
          <p className="muted">
            No fixtures yet. Add one and its opponent, date, venue and selected side will be printed
            on the poster. Posters are generated from a fixture, so this is the place to start.
          </p>
        </div>
      )}

      {generatingFor && (
        <GeneratePosterDialog
          tournament={tournament}
          match={generatingFor}
          open={generatingFor !== undefined}
          onQueued={onPosterQueued}
          onClose={() => setGeneratingFor(undefined)}
        />
      )}

      <ul className="list">
        {matches.map((match) => (
          <li key={match.id} className="card list__item">
            <div>
              <p className="pill">{match.roundName || `Week ${match.week}`}</p>
              <h3>v {match.opponent}</h3>
              <p className="muted small">
                {new Date(match.startsAt).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {match.venue ? ` · ${match.venue}` : ''} · {match.isHome ? 'Home' : 'Away'}
              </p>
              {match.players.length > 0 && (
                <ol className="squad">
                  {match.players.map((player, index) => (
                    <li key={`${player.name}-${index}`}>{player.name}</li>
                  ))}
                </ol>
              )}
            </div>
            <div className="list__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => setGeneratingFor(match)}
              >
                Generate poster
              </button>
              <button type="button" className="button button--ghost" onClick={() => startEdit(match)}>
                Edit
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={() => {
                  if (window.confirm(`Delete the fixture against ${match.opponent}?`)) {
                    void deleteMatch.mutateAsync(match.id);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {form && (
        <form className="card form" onSubmit={(e) => void submit(e)}>
          <h3>{editingId ? 'Edit fixture' : 'New fixture'}</h3>

          <div className="grid grid--2">
            <label>
              Opponent
              <input
                required
                value={form.opponent}
                placeholder="Grangemouth Railway"
                onChange={(e) => setForm({ ...form, opponent: e.target.value })}
              />
            </label>
            <label>
              Venue
              <input
                value={form.venue ?? ''}
                placeholder="Grangemouth Railway"
                onChange={(e) => setForm({ ...form, venue: e.target.value })}
              />
            </label>
            <label>
              Date and time
              <input
                type="datetime-local"
                required
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              />
            </label>
            <label>
              Week
              <input
                type="number"
                min={1}
                max={tournament.weeks}
                value={form.week}
                onChange={(e) => setForm({ ...form, week: Number(e.target.value) })}
              />
            </label>
            <label>
              Round name (optional)
              <input
                value={form.roundName ?? ''}
                placeholder="Quarter Final"
                onChange={(e) => setForm({ ...form, roundName: e.target.value })}
              />
            </label>
            <label className="inline checkbox">
              <input
                type="checkbox"
                checked={form.isHome}
                onChange={(e) => setForm({ ...form, isHome: e.target.checked })}
              />
              Played at home
            </label>
            <label className="span-2">
              Notes (optional)
              <input
                value={form.notes ?? ''}
                placeholder="Bus leaves the club at 5pm"
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </label>
          </div>

          <fieldset className="players">
            <legend>Selected side, in poster order</legend>
            <p className="muted small">
              These are numbered down the poster exactly as ordered here.
            </p>

            {form.players.map((player, index) => (
              <div key={index} className="players__row">
                <span className="players__number">{index + 1}</span>
                <input
                  value={player.name}
                  placeholder="Player name"
                  onChange={(e) => updatePlayer(index, { name: e.target.value })}
                />
                <select
                  value={player.position}
                  onChange={(e) => updatePlayer(index, { position: e.target.value as PlayerPosition })}
                >
                  {POSITIONS.map((position) => (
                    <option key={position} value={position}>
                      {position === 'Unspecified' ? 'No position' : position}
                    </option>
                  ))}
                </select>
                <div className="players__controls">
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`Move ${player.name || 'player'} up`}
                    disabled={index === 0}
                    onClick={() => movePlayer(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    aria-label={`Move ${player.name || 'player'} down`}
                    disabled={index === form.players.length - 1}
                    onClick={() => movePlayer(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => setForm({ ...form, players: form.players.filter((_, i) => i !== index) })}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="button button--ghost"
              onClick={() =>
                setForm({ ...form, players: [...form.players, { name: '', position: 'Unspecified' }] })
              }
            >
              Add player
            </button>
          </fieldset>

          <ErrorBanner error={saveMatch.error} />

          <div className="row">
            <button type="submit" className="button button--primary" disabled={saveMatch.isPending}>
              {saveMatch.isPending ? 'Saving...' : 'Save fixture'}
            </button>
            <button type="button" className="button button--ghost" onClick={() => setForm(undefined)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
